import {
  Alert,
  Badge,
  Flex,
  IconButton,
  IconEditLine,
  IconFolderLine,
  IconOpenFolderLine,
  Pill,
  ScreenReaderContent,
  Text,
  ToggleGroup,
  Tooltip,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { memo, useContext, useState } from "react";
import { FeatureContext } from "../../../../utils/Context";
import { Selector } from "../../../Selectors/Selector";
import CourseReviewCyclesModal from "../CourseReviewCycles/CourseReviewCyclesModal";
import { ReviewCycleContext } from "../Review";
import OfferingToggle from "./OfferingToggle";

const CourseToggle = memo(({ course }) => {
  const features = useContext(FeatureContext);
  const { courseReviewCycles } = useContext(ReviewCycleContext);

  const associatedCycle = courseReviewCycles.find((cycle) =>
    cycle.reviewables.some((c) => c.id === course.id)
  );

  const finalizeFlag =
    features.find((f) => f.name === "finalize_results_flag")?.value === "TRUE";

  const getOfferingOptions = () => {
    let offerings = [];
    if (course.offerings && course.offerings.length > 0) {
      course.offerings.forEach((offering) => {
        offering.label = `${offering.code}: ${offering.course_summary.representative_section_name}`;
      });
      offerings.push(
        course.offerings.find((offering) => !!offering.is_default)
      );
      offerings = offerings.concat(
        course.offerings.filter((offering) => !!offering.has_unique_los)
      );

      return offerings;
    }
  };
  const offeringOptions = getOfferingOptions();

  const [selectedOffering, setSelectedOffering] = useState(
    course.offerings.find((offering) => !!offering.is_default) ?? null
  );

  const getNumUnapprovedOutcomes = () => {
    let numUnapproved = 0;
    if (course.offerings && course.offerings.length > 0) {
      const filteredOfferings = course.offerings.filter(
        (offering) => offering.is_default || offering.has_unique_los
      );
      filteredOfferings.forEach((offering) => {
        offering.learning_outcomes.forEach((lo) => {
          if (
            lo.unapproved &&
            lo.context_maps.find((cm) => cm.context_id === offering.id)
          ) {
            numUnapproved++;
          }
        });
      });
      return numUnapproved;
    }
  };
  const [numUnapprovedOutcomes, setNumUnapprovedOutcomes] = useState(
    getNumUnapprovedOutcomes()
  );

  const handleOfferingSelect = (offeringId) => {
    const offering = course.offerings.find(
      (offering) => offering.id === offeringId
    );
    setSelectedOffering(offering);
  };

  const handleArchiveOutcome = (outcome, term) => {
    setSelectedOffering((prev) => {
      const offering = { ...prev };
      const index = offering.learning_outcomes.findIndex(
        (lo) => lo.id === outcome.id
      );
      offering.learning_outcomes[index].archive_term = term;
      return offering;
    });
  };

  return (
    <ToggleGroup
      iconExpanded={IconOpenFolderLine}
      icon={IconFolderLine}
      defaultExpanded
      toggleLabel={`${t("global.showingData")} ${course.code}`}
      summary={
        <Flex gap="small" wrap="wrap">
          <Flex.Item>
            <Text>{course.code}</Text>
          </Flex.Item>
          <Flex.Item>
            <Tooltip
              offsetX={10}
              placement="top start"
              color="primary-inverse"
              renderTip={
                <Text weight="bold" size="small">
                  {t("analyze.numOfOfferings", { code: course.code })}:{" "}
                  <Text size="small" weight="normal">
                    {course.offerings.length}
                  </Text>
                  <br />
                  {t("analyze.numOfUniqueOutcomes")}:{" "}
                  <Text size="small" weight="normal">
                    {offeringOptions.length}
                  </Text>
                </Text>
              }
            >
              <Flex.Item>
                <Badge standalone count={course.offerings.length} />
              </Flex.Item>
            </Tooltip>
          </Flex.Item>

          {course.offerings.length > 0 && (
            <Flex.Item shouldGrow={!associatedCycle}>
              <label htmlFor={`offering-selector-${course.code}`}>
                <ScreenReaderContent>
                  {t("results.offering")}
                </ScreenReaderContent>
              </label>
              <Selector
                width="400px"
                id={`offering-selector-${course.code}`}
                options={offeringOptions}
                size="small"
                selectHandler={handleOfferingSelect}
                selectedItemId={selectedOffering?.id}
              />
            </Flex.Item>
          )}
          {finalizeFlag && associatedCycle && (
            <Flex.Item shouldGrow>
              <Flex wrap="wrap">
                <Flex.Item>{t("courseReviewCycle.reviewCycle")}:</Flex.Item>
                <Flex.Item margin="0 small 0 xx-small">
                  <Pill color="success">
                    {associatedCycle.duration},{" "}
                    {t("courseReviewCycle.starting")}{" "}
                    {associatedCycle.start_date.name}
                  </Pill>
                </Flex.Item>
                <Flex.Item>
                  <EditCyclesButton
                    cycle={associatedCycle}
                    courseCode={course.code}
                  />
                </Flex.Item>
              </Flex>
            </Flex.Item>
          )}
          {course.offerings.length > 0 &&
            course.offerings.find((o) => o.has_blueprint_section) && (
              <Flex.Item>
                <Pill color="info">{t("blueprint.blueprint")}</Pill>
              </Flex.Item>
            )}
          {numUnapprovedOutcomes > 0 && (
            <Flex.Item>
              <Pill color="warning">{`${numUnapprovedOutcomes} ${t(
                "dashboard.unapprovedOutcomes"
              )}`}</Pill>
            </Flex.Item>
          )}
        </Flex>
      }
      size="small"
      margin="0 0 small 0"
    >
      <View display="block" padding="small">
        {course.offerings.length === 0 ? (
          <Alert variant="info">{t("analyze.noOfferings")}</Alert>
        ) : (
          <OfferingToggle
            offering={selectedOffering}
            course={course}
            setNumUnapprovedOutcomes={setNumUnapprovedOutcomes}
            handleArchiveOutcome={handleArchiveOutcome}
          />
        )}
      </View>
    </ToggleGroup>
  );
});

const EditCyclesButton = ({ cycle, courseCode }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton
        screenReaderLabel={t("courseReviewCycle.editCycleFor", {
          course: courseCode,
        })}
        renderIcon={IconEditLine}
        size="small"
        onClick={() => setOpen(true)}
      />
      <CourseReviewCyclesModal open={open} setOpen={setOpen} cycle={cycle} />
    </>
  );
};

export default CourseToggle;
